import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import Layout from '../components/Layout';
import SignInModal from '../components/SignInModal';
import { useAuth } from '../contexts/AuthContext';
import { useUserCard } from '../hooks/useUserCard';
import { 
  Eye, TrendingUp, Users, Share2, Copy, 
  CreditCard, Sparkles, Gift, ArrowRight,
  ExternalLink, MousePointerClick, UserPlus, Check,
  ChevronDown, Loader2, BarChart3, Link as LinkIcon,
  ShoppingBag, RefreshCw, MapPin, Crown, Trash2, AlertTriangle, Globe
} from 'lucide-react';
import QRCodeWithLogo from '../components/QRCodeWithLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' }
  }
};

// Colors for charts
const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

// Stats Card Component
const StatsCard = ({ icon: Icon, title, value, subtitle, trend, color = 'amber', loading = false }) => {
  const colorClasses = {
    amber: 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200',
    teal: 'bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200',
    blue: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200',
    purple: 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'
  };
  
  const iconColors = {
    amber: 'bg-amber-600 text-white',
    teal: 'bg-teal-600 text-white',
    blue: 'bg-blue-600 text-white',
    purple: 'bg-purple-600 text-white'
  };

  return (
    <motion.div variants={itemVariants}>
      <Card className={`${colorClasses[color]} border hover:shadow-md transition-all duration-300`}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start justify-between">
            <div className={`w-10 h-10 rounded-xl ${iconColors[color]} flex items-center justify-center shadow-sm`}>
              <Icon className="w-5 h-5" />
            </div>
            {trend !== undefined && trend !== null && (
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                trend > 0 ? 'bg-green-100 text-green-700' : trend < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {trend > 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
          <div className="mt-3">
            {loading ? (
              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded" />
            ) : (
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{value?.toLocaleString() || 0}</p>
            )}
            <p className="text-sm text-gray-600 mt-1">{title}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Card Selector Component
const CardSelector = ({ cards, selectedCard, onSelect, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (loading || !cards || cards.length <= 1) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-amber-400 hover:shadow-md transition-all w-full sm:w-auto"
        data-testid="card-selector"
      >
        {selectedCard?.profileImage ? (
          <img 
            src={selectedCard.profileImage} 
            alt={selectedCard.name || 'Card'}
            className="w-8 h-8 rounded-full object-cover border-2 border-amber-200"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 flex items-center justify-center">
            <span className="text-white text-sm font-bold">
              {selectedCard?.name?.charAt(0) || 'C'}
            </span>
          </div>
        )}
        <div className="text-left flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{selectedCard?.name || 'Select Card'}</p>
          <p className="text-xs text-gray-500 truncate">@{selectedCard?.username}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden min-w-[250px]">
            <div className="p-2 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-medium text-gray-500 px-2">Your Cards ({cards.length})</p>
            </div>
            {cards.map((card) => (
              <button
                key={card.id || card._id}
                onClick={() => {
                  onSelect(card);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                  (card.id || card._id) === (selectedCard?.id || selectedCard?._id) ? 'bg-amber-50' : ''
                }`}
              >
                {card.profileImage ? (
                  <img 
                    src={card.profileImage} 
                    alt={card.name || 'Card'}
                    className="w-10 h-10 rounded-full object-cover border border-gray-200"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{card.name?.charAt(0) || 'C'}</span>
                  </div>
                )}
                <div className="text-left flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{card.name}</p>
                  <p className="text-xs text-gray-500 truncate">@{card.username}</p>
                </div>
                {(card.id || card._id) === (selectedCard?.id || selectedCard?._id) && (
                  <Check className="w-5 h-5 text-amber-600" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Profile Preview Card Component
const ProfilePreviewCard = ({ card }) => {
  if (!card) return null;
  
  return (
    <motion.div variants={itemVariants}>
      <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
        <CardContent className="p-0">
          <div 
            className="h-20 bg-gradient-to-r from-amber-500 to-yellow-400"
            style={card.coverImage ? { backgroundImage: `url(${card.coverImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
          />
          <div className="p-4 -mt-8 relative">
            <div className="w-16 h-16 rounded-full border-4 border-white overflow-hidden bg-gray-100 mx-auto shadow-md">
              {card.profileImage ? (
                <img 
                  src={card.profileImage} 
                  alt={card.name || 'Profile'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-500">
                  <span className="text-xl font-bold text-white">
                    {card.name?.charAt(0) || 'U'}
                  </span>
                </div>
              )}
            </div>
            <div className="text-center mt-2">
              <h3 className="text-lg font-bold text-gray-900">{card.name || 'Your Card'}</h3>
              <p className="text-sm text-gray-600">{card.title || card.designation}</p>
              {card.company && <p className="text-xs text-gray-500">{card.company}</p>}
            </div>
            <div className="mt-4 flex gap-2">
              <Link to={`/card/${card.username}`} className="flex-1">
                <Button 
                  variant="outline" 
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid="view-card-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View
                </Button>
              </Link>
              <Link to={`/edit/${card.username}`} className="flex-1">
                <Button 
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="edit-card-btn"
                >
                  Edit
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Quick Share Component
const QuickShareSection = ({ card }) => {
  const [copied, setCopied] = useState(false);
  const cardUrl = card ? `${window.location.origin}/bio/${card.username}` : '';
  
  // Check if premium card
  const isPremiumCard = card?.is_premium_card === true && 
    card?.premium_expires_at && 
    new Date(card.premium_expires_at) > new Date();
  
  // Get QR color from card settings
  const qrColor = card?.qrColor || '#d97706';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(cardUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async (platform) => {
    const shareData = {
      title: card?.name || 'My Digital Card',
      text: `Check out my digital business card!`,
      url: cardUrl
    };

    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${shareData.text} ${cardUrl}`)}`);
    } else if (platform === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(cardUrl)}`);
    }
  };

  if (!card) return null;

  return (
    <motion.div variants={itemVariants}>
      <Card className="bg-white border border-gray-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-gray-900">Quick Share</h3>
            {isPremiumCard && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                Premium
              </span>
            )}
          </div>
          
          {/* QR Code with card's color and logo for premium */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4 flex justify-center border border-gray-100">
            <QRCodeWithLogo 
              value={cardUrl}
              size={120}
              qrColor={qrColor}
              showLogo={isPremiumCard}
              logoUrl={isPremiumCard ? (card.companyLogo || card.profileImage) : null}
            />
          </div>
          
          {/* Copy Link */}
          <div className="flex gap-2 mb-3">
            <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600 truncate border border-gray-200">
              {cardUrl}
            </div>
            <Button
              onClick={handleCopyLink}
              variant="outline"
              size="sm"
              className={`border-gray-300 ${copied ? 'text-green-600 border-green-500 bg-green-50' : 'text-gray-600'}`}
              data-testid="copy-link-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Share Buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleShare('native')}
              className="flex flex-col items-center gap-1 p-2 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors border border-amber-100"
              data-testid="share-native-btn"
            >
              <Share2 className="w-4 h-4 text-amber-600" />
              <span className="text-[10px] text-gray-600">Share</span>
            </button>
            <button
              onClick={() => handleShare('whatsapp')}
              className="flex flex-col items-center gap-1 p-2 rounded-lg bg-green-50 hover:bg-green-100 transition-colors border border-green-100"
              data-testid="share-whatsapp-btn"
            >
              <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="text-[10px] text-gray-600">WhatsApp</span>
            </button>
            <button
              onClick={() => handleShare('linkedin')}
              className="flex flex-col items-center gap-1 p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-100"
              data-testid="share-linkedin-btn"
            >
              <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <span className="text-[10px] text-gray-600">LinkedIn</span>
            </button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Mini Upsell Component (non-aggressive)
const MiniUpsell = () => {
  const navigate = useNavigate();
  
  return (
    <motion.div variants={itemVariants}>
      <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-sm flex-shrink-0">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-sm">Need more cards?</h4>
              <p className="text-xs text-gray-600">Get premium NFC smart cards</p>
            </div>
            <Button 
              size="sm"
              onClick={() => navigate('/shop')}
              className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
              data-testid="shop-upsell-btn"
            >
              Shop
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Main Dashboard Component
const DashboardV2 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userCard, loading: cardLoading } = useUserCard();
  
  const [allCards, setAllCards] = useState([]);
  const [allCardsAnalytics, setAllCardsAnalytics] = useState({});
  const [selectedCard, setSelectedCard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [totalStats, setTotalStats] = useState({ totalViews: 0, totalClicks: 0, contactSaves: 0 });
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(!user);
  const [timeRange, setTimeRange] = useState('30d');
  const [error, setError] = useState(null);
  
  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch all user cards and their total analytics
  useEffect(() => {
    const fetchAllCards = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Ensure Firebase auth is ready before making API calls
        // This prevents race conditions where the token isn't available yet
        if (user.getIdToken) {
          try {
            await user.getIdToken(); // Force token refresh to ensure auth is ready
          } catch (tokenError) {
            console.error('Error getting auth token:', tokenError);
          }
        }
        
        const { cardAPI, analyticsAPI } = await import('../services/api');
        const cards = await cardAPI.getMyCards();
        console.log('📇 Fetched cards:', cards?.length || 0, cards);
        setAllCards(cards || []);
        
        // Set the first card as selected if we have cards
        if (cards && cards.length > 0) {
          setSelectedCard(cards[0]);
          
          // Fetch batch analytics for all cards to show totals
          const cardIds = cards.map(c => c.id || c._id).filter(Boolean);
          console.log('📊 Card IDs for analytics:', cardIds);
          
          if (cardIds.length > 0) {
            try {
              const batchViews = await analyticsAPI.getBatchCardViews(cardIds);
              console.log('📈 Batch views:', batchViews);
              
              // Store individual card analytics
              setAllCardsAnalytics(batchViews || {});
              
              // Calculate totals
              const totalViews = Object.values(batchViews).reduce((sum, v) => sum + (v || 0), 0);
              setTotalStats(prev => ({ ...prev, totalViews }));
            } catch (err) {
              console.error('Error fetching batch views:', err);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching cards:', error);
        setError('Failed to load your cards');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllCards();
  }, [user]);

  // Fetch analytics for selected card
  const fetchAnalytics = useCallback(async () => {
    if (!selectedCard) {
      setAnalytics(null);
      return;
    }
    
    setAnalyticsLoading(true);
    setError(null);
    
    try {
      const { analyticsAPI } = await import('../services/api');
      const cardId = selectedCard.id || selectedCard._id;
      
      console.log('📊 Fetching analytics for card:', cardId);
      
      const [viewsData, clicksData, savesData] = await Promise.all([
        analyticsAPI.getCardViews(cardId),
        analyticsAPI.getCardLinkClicks(cardId),
        analyticsAPI.getCardSaves(cardId).catch(() => ({ total_saves: 0 }))
      ]);

      console.log('📈 Views data:', viewsData);
      console.log('🔗 Clicks data:', clicksData);
      console.log('💾 Saves data:', savesData);

      // Process views within the time range
      const allViews = viewsData.views || [];
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - days);
      startDate.setHours(0, 0, 0, 0);

      // Filter views within the time range
      const filteredViews = allViews.filter(view => {
        if (!view.viewedAt) return false;
        const viewDate = new Date(view.viewedAt);
        if (isNaN(viewDate.getTime())) return false;
        return viewDate >= startDate;
      });

      // Calculate stats based on filtered views
      const filteredTotalViews = filteredViews.length;
      const uniqueIPs = new Set(filteredViews.map(v => v.ipAddress || v.ip).filter(Boolean));
      const filteredUniqueViews = uniqueIPs.size || filteredViews.length;
      
      // Get contact saves from the actual contact_saves collection
      const contactSaves = savesData.total_saves || 0;
      
      // Process daily views for chart
      const dailyData = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateString = date.toISOString().split('T')[0];
        
        const dayViews = filteredViews.filter(view => {
          const viewDate = new Date(view.viewedAt);
          return viewDate.toISOString().split('T')[0] === dateString;
        }).length;
        
        dailyData.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          views: dayViews
        });
      }

      // Process link stats for pie chart - filter by time range too
      const allClicks = clicksData.clicks || [];
      const filteredClicks = allClicks.filter(click => {
        if (!click.clickedAt) return false;
        const clickDate = new Date(click.clickedAt);
        if (isNaN(clickDate.getTime())) return false;
        return clickDate >= startDate;
      });
      
      // Group clicks by link type
      const clicksByType = {};
      filteredClicks.forEach(click => {
        const type = click.linkType || 'other';
        clicksByType[type] = (clicksByType[type] || 0) + 1;
      });
      
      const linkStats = Object.entries(clicksByType).map(([type, count], idx) => ({
        name: type.charAt(0).toUpperCase() + type.slice(1),
        value: count,
        color: COLORS[idx % COLORS.length]
      }));
      
      const filteredTotalClicks = filteredClicks.length;

      // Process location data from views
      const locationCounts = {};
      filteredViews.forEach(view => {
        let location = 'Unknown';
        if (view.city && view.city !== 'Unknown') {
          location = view.country && view.country !== 'Unknown' 
            ? `${view.city}, ${view.country}` 
            : view.city;
        } else if (view.country && view.country !== 'Unknown') {
          location = view.country;
        } else if (view.viewerInfo?.location?.city) {
          const loc = view.viewerInfo.location;
          location = loc.country && loc.country !== 'Unknown'
            ? `${loc.city}, ${loc.country}`
            : loc.city;
        }
        locationCounts[location] = (locationCounts[location] || 0) + 1;
      });
      
      // Sort locations by count and get top 5
      const topLocations = Object.entries(locationCounts)
        .map(([location, count]) => ({ location, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setAnalytics({
        totalViews: filteredTotalViews,
        uniqueViews: filteredUniqueViews,
        totalClicks: filteredTotalClicks,
        linkStats,
        dailyData,
        contactSaves,
        recentViews: filteredViews.slice(0, 5),
        topLocations
      });
      
      // Update total stats
      setTotalStats(prev => ({
        ...prev,
        contactSaves
      }));
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics({
        totalViews: 0,
        uniqueViews: 0,
        totalClicks: 0,
        linkStats: [],
        dailyData: [],
        contactSaves: 0,
        recentViews: [],
        topLocations: []
      });
    } finally {
      setAnalyticsLoading(false);
    }
  }, [selectedCard, timeRange]);

  useEffect(() => {
    if (selectedCard) {
      fetchAnalytics();
    }
  }, [selectedCard, timeRange, fetchAnalytics]);

  // Handle opening delete confirmation modal
  const openDeleteModal = (card) => {
    setCardToDelete(card);
    setDeleteConfirmText('');
    setDeleteModalOpen(true);
  };

  // Handle card deletion
  const handleDeleteCard = async () => {
    if (!cardToDelete || deleteConfirmText.toLowerCase() !== 'delete') return;
    
    setIsDeleting(true);
    try {
      const { cardAPI } = await import('../services/api');
      await cardAPI.delete(cardToDelete.username);
      
      // Remove card from state
      const updatedCards = allCards.filter(c => (c.id || c._id) !== (cardToDelete.id || cardToDelete._id));
      setAllCards(updatedCards);
      
      // If deleted card was selected, select another card or null
      if ((selectedCard?.id || selectedCard?._id) === (cardToDelete.id || cardToDelete._id)) {
        setSelectedCard(updatedCards.length > 0 ? updatedCards[0] : null);
      }
      
      // Close modal and reset state
      setDeleteModalOpen(false);
      setCardToDelete(null);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting card:', error);
      alert('Failed to delete card. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Memoized chart data
  const chartData = useMemo(() => {
    return analytics?.dailyData || [];
  }, [analytics]);

  // Show sign in modal if not logged in
  useEffect(() => {
    if (!user) {
      setShowSignInModal(true);
    }
  }, [user]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg">
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-sm font-semibold text-amber-600">{payload[0].value} views</p>
        </div>
      );
    }
    return null;
  };

  const hasCards = allCards.length > 0;

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <div className="min-h-screen bg-gray-50 py-4 sm:py-6 px-3 sm:px-4 lg:px-6" data-testid="dashboard-v2">
        {user ? (
          <motion.div 
            className="max-w-7xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Header */}
            <motion.div variants={itemVariants} className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    Welcome back, {user.displayName?.split(' ')[0] || 'there'}!
                  </h1>
                  <p className="text-sm sm:text-base text-gray-600 mt-1">
                    {loading ? 'Loading your cards...' : 
                     hasCards ? `You have ${allCards.length} digital ${allCards.length === 1 ? 'card' : 'cards'}` : 
                     "Create your first digital card"}
                  </p>
                </div>
                
                {/* Refresh Button Only */}
                <div className="flex items-center gap-2">
                  {selectedCard && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchAnalytics}
                      disabled={analyticsLoading}
                      className="border-gray-300"
                      data-testid="refresh-analytics-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${analyticsLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>

            {loading ? (
              /* Loading State */
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 text-amber-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading your dashboard...</p>
                </div>
              </div>
            ) : hasCards && selectedCard ? (
              /* ====== USER HAS CARDS - Analytics View ====== */
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Column - Analytics */}
                <div className="lg:col-span-8 space-y-6">
                  
                  {/* Your Cards Section - Always Show */}
                  <motion.div variants={itemVariants}>
                    <Card className="bg-white border border-gray-200 shadow-sm">
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-gray-900">Your Cards ({allCards.length})</h3>
                          <Link to="/shop">
                            <Button size="sm" variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                              <ShoppingBag className="w-4 h-4 mr-1" />
                              Shop
                            </Button>
                          </Link>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          {allCards.map((card) => {
                            // Only show NFC badge if nfc_code actually exists and is not empty
                            const isNFC = !!(card.nfc_code && card.nfc_code.trim() !== '');
                            const isSelected = (card.id || card._id) === (selectedCard?.id || selectedCard?._id);
                            
                            // Check card premium status
                            const isPremiumCard = card.is_premium_card === true;
                            const premiumExpiresAt = card.premium_expires_at ? new Date(card.premium_expires_at) : null;
                            const now = new Date();
                            const hasPremium = isPremiumCard && premiumExpiresAt && premiumExpiresAt > now;
                            const daysLeft = premiumExpiresAt ? Math.ceil((premiumExpiresAt - now) / (1000 * 60 * 60 * 24)) : 0;
                            const isExpiringSoon = hasPremium && daysLeft <= 7;
                            
                            return (
                              <button
                                key={card.id || card._id}
                                onClick={() => setSelectedCard(card)}
                                className={`group relative flex flex-col items-center p-3 rounded-xl border transition-all ${
                                  isSelected
                                    ? 'border-amber-500 bg-amber-50 shadow-sm'
                                    : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                                }`}
                              >
                                {/* Premium Status Badge - Top Left */}
                                {hasPremium ? (
                                  <span className={`absolute top-2 left-2 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                    isExpiringSoon ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                                  }`}>
                                    {isExpiringSoon ? `${daysLeft}d left` : 'Premium'}
                                  </span>
                                ) : (
                                  <Link 
                                    to="/card-premium" 
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-2 left-2 bg-gray-100 text-gray-500 text-[9px] font-medium px-1.5 py-0.5 rounded hover:bg-amber-100 hover:text-amber-700 transition-colors"
                                  >
                                    Free
                                  </Link>
                                )}
                                
                                {/* NFC Badge - Top Right */}
                                {isNFC ? (
                                  <span className="absolute top-2 right-2 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                    NFC
                                  </span>
                                ) : (
                                  <Link 
                                    to="/shop" 
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-[10px] font-medium px-1.5 py-0.5 rounded hover:bg-amber-200 transition-colors"
                                  >
                                    Get NFC
                                  </Link>
                                )}
                                
                                {/* Profile Image */}
                                {card.profileImage ? (
                                  <img 
                                    src={card.profileImage} 
                                    alt={card.name || 'Card'}
                                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 mb-2 mt-3"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 flex items-center justify-center mb-2 mt-3">
                                    <span className="text-white text-base font-bold">{card.name?.charAt(0) || 'C'}</span>
                                  </div>
                                )}
                                
                                {/* Name & Designation */}
                                <p className="font-medium text-gray-900 text-sm truncate w-full text-center">{card.name}</p>
                                <p className="text-xs text-gray-500 truncate w-full text-center">
                                  {card.designation || card.title || 'Digital Profile'}
                                </p>
                                
                                {/* View Button - Bottom Right */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`/bio/${card.username}`, '_blank');
                                  }}
                                  className="absolute bottom-2 right-2 p-1.5 rounded-full bg-gray-100 hover:bg-amber-100 text-gray-400 hover:text-amber-600 transition-colors opacity-0 group-hover:opacity-100"
                                  data-testid={`view-card-btn-${card.username}`}
                                  title="View bio page"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </button>
                                
                                {/* Selected Indicator */}
                                {isSelected && (
                                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  
                  {/* Check if selected card has premium for analytics */}
                  {(() => {
                    const selectedCardHasPremium = selectedCard?.is_premium_card === true && 
                      selectedCard?.premium_expires_at && 
                      new Date(selectedCard.premium_expires_at) > new Date();
                    
                    if (!selectedCardHasPremium) {
                      return (
                        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200">
                          <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                              <BarChart3 className="w-8 h-8 text-amber-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              Unlock Analytics
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                              Get Card Premium to access detailed analytics including views, clicks, and visitor insights.
                            </p>
                            <Link to={`/card-premium?card=${selectedCard?.username}`}>
                              <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white">
                                <Crown className="w-4 h-4 mr-2" />
                                Get Card Premium
                              </Button>
                            </Link>
                          </CardContent>
                        </Card>
                      );
                    }
                    
                    return (
                      <>
                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                          <StatsCard 
                            icon={Eye}
                            title="Total Views"
                            value={analytics?.totalViews || 0}
                            color="amber"
                            loading={analyticsLoading}
                          />
                          <StatsCard 
                            icon={Users}
                            title="Unique Visitors"
                            value={analytics?.uniqueViews || 0}
                            color="teal"
                            loading={analyticsLoading}
                          />
                          <StatsCard 
                            icon={MousePointerClick}
                            title="Link Clicks"
                            value={analytics?.totalClicks || 0}
                            color="blue"
                            loading={analyticsLoading}
                          />
                          <StatsCard 
                            icon={UserPlus}
                            title="Contact Saves"
                            value={analytics?.contactSaves || 0}
                            color="purple"
                            loading={analyticsLoading}
                          />
                        </div>

                        {/* Views Chart */}
                        <motion.div variants={itemVariants}>
                          <Card className="bg-white border border-gray-200 shadow-sm">
                            <CardContent className="p-4 sm:p-5">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                                <div className="flex items-center gap-2">
                                  <BarChart3 className="w-5 h-5 text-amber-600" />
                                  <h3 className="font-semibold text-gray-900">Profile Views</h3>
                                  {analytics?.totalViews > 0 && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                      {analytics.totalViews} total
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                                  {['7d', '30d', '90d'].map((range) => (
                                    <button
                                      key={range}
                                      onClick={() => setTimeRange(range)}
                                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                        timeRange === range 
                                          ? 'bg-amber-600 text-white shadow-sm' 
                                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                                      }`}
                                      data-testid={`time-range-${range}`}
                                    >
                                      {range === '7d' ? '7D' : range === '30d' ? '30D' : '90D'}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="h-[220px] sm:h-[280px]">
                                {analyticsLoading ? (
                                  <div className="h-full flex items-center justify-center">
                                    <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
                                  </div>
                                ) : chartData.length > 0 && chartData.some(d => d.views > 0) ? (
                                  <ResponsiveContainer width="100%" height={220} minHeight={50} debounce={50}>
                                    <AreaChart data={chartData}>
                                      <defs>
                                        <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/>
                                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05}/>
                                        </linearGradient>
                                      </defs>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                      <XAxis 
                                        dataKey="date" 
                                        stroke="#9ca3af" 
                                        tick={{ fill: '#6b7280', fontSize: 11 }}
                                        tickLine={false}
                                      />
                                      <YAxis 
                                        stroke="#9ca3af" 
                                        tick={{ fill: '#6b7280', fontSize: 11 }}
                                        tickLine={false}
                                        axisLine={false}
                                        allowDecimals={false}
                                      />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Area 
                                        type="monotone" 
                                        dataKey="views" 
                                        stroke="#f59e0b" 
                                        strokeWidth={2}
                                        fill="url(#viewsGradient)" 
                                      />
                                    </AreaChart>
                                  </ResponsiveContainer>
                                ) : (
                                  <div className="h-full flex flex-col items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
                                    <BarChart3 className="w-12 h-12 text-gray-300 mb-3" />
                                    <p className="text-sm font-medium">No views yet</p>
                                    <p className="text-xs text-gray-400 mt-1">Share your card to start tracking</p>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </>
                    );
                  })()}

                  {/* Link Clicks Section */}
                  <motion.div variants={itemVariants}>
                    <Card className="bg-white border border-gray-200 shadow-sm">
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-center gap-2 mb-5">
                          <LinkIcon className="w-5 h-5 text-blue-600" />
                          <h3 className="font-semibold text-gray-900">Link Clicks</h3>
                          {analytics?.totalClicks > 0 && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              {analytics.totalClicks} total
                            </span>
                          )}
                        </div>
                        
                        {analyticsLoading ? (
                          <div className="h-[180px] flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                          </div>
                        ) : analytics?.linkStats?.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Pie Chart */}
                            <div className="h-[180px]">
                              <ResponsiveContainer width="100%" height={180} minHeight={50} debounce={50}>
                                <PieChart>
                                  <Pie
                                    data={analytics.linkStats}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={40}
                                    outerRadius={70}
                                    paddingAngle={2}
                                    dataKey="value"
                                  >
                                    {analytics.linkStats.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            
                            {/* Legend */}
                            <div className="space-y-2">
                              {analytics.linkStats.slice(0, 5).map((stat, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-3 h-3 rounded-full" 
                                      style={{ backgroundColor: stat.color }}
                                    />
                                    <span className="text-sm text-gray-700 capitalize">{stat.name}</span>
                                  </div>
                                  <span className="text-sm font-bold text-gray-900">{stat.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="h-[180px] flex flex-col items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
                            <LinkIcon className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-sm font-medium">No clicks yet</p>
                            <p className="text-xs text-gray-400 mt-1">Clicks appear when visitors tap your links</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Visitor Locations Section */}
                  <motion.div variants={itemVariants}>
                    <Card className="bg-white border border-gray-200 shadow-sm">
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <MapPin className="w-5 h-5 text-red-500" />
                          <h3 className="font-semibold text-gray-900">Visitor Locations</h3>
                        </div>
                        
                        {analyticsLoading ? (
                          <div className="h-[140px] flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
                          </div>
                        ) : analytics?.topLocations?.length > 0 ? (
                          <div className="space-y-3">
                            {analytics.topLocations.map((loc, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center flex-shrink-0">
                                  <MapPin className="w-4 h-4 text-red-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{loc.location}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-gradient-to-r from-red-400 to-orange-400 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min((loc.count / analytics.totalViews) * 100, 100)}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                                      {loc.count} {loc.count === 1 ? 'view' : 'views'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="h-[140px] flex flex-col items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
                            <MapPin className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-sm font-medium">No location data yet</p>
                            <p className="text-xs text-gray-400 mt-1">Locations appear when visitors view your card</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Mini Upsell */}
                  <MiniUpsell />
                </div>

                {/* Right Column - Profile & Share */}
                <div className="lg:col-span-4 space-y-6">
                  <ProfilePreviewCard card={selectedCard} />
                  <QuickShareSection card={selectedCard} />
                  
                  {/* Quick Actions */}
                  <motion.div variants={itemVariants}>
                    <Card className="bg-white border border-gray-200 shadow-sm">
                      <CardContent className="p-4 sm:p-5">
                        <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
                        <div className="space-y-2">
                          {selectedCard?.username && (
                            <>
                              <Link 
                                to={`/bio/${selectedCard.username}`}
                                className="block"
                              >
                                <Button variant="outline" className="w-full justify-start border-gray-200 text-gray-700 hover:bg-gray-50">
                                  <ExternalLink className="w-4 h-4 mr-2 text-amber-600" />
                                  View Bio Page
                                </Button>
                              </Link>
                              <a 
                                href={`/web/${selectedCard.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <Button variant="outline" className="w-full justify-start border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300">
                                  <Globe className="w-4 h-4 mr-2 text-blue-600" />
                                  View Mini Website
                                </Button>
                              </a>
                            </>
                          )}
                          <Link to="/create" className="block">
                            <Button variant="outline" className="w-full justify-start border-gray-200 text-gray-700 hover:bg-gray-50">
                              <CreditCard className="w-4 h-4 mr-2 text-amber-600" />
                              Create New Card
                            </Button>
                          </Link>
                          <Link to="/contacts" className="block">
                            <Button variant="outline" className="w-full justify-start border-gray-200 text-gray-700 hover:bg-gray-50">
                              <Users className="w-4 h-4 mr-2 text-amber-600" />
                              View Contacts
                            </Button>
                          </Link>
                          <Link to="/shop" className="block">
                            <Button variant="outline" className="w-full justify-start border-gray-200 text-gray-700 hover:bg-gray-50">
                              <Sparkles className="w-4 h-4 mr-2 text-amber-600" />
                              Browse Designs
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              </div>
            ) : (
              /* ====== USER HAS NO CARDS ====== */
              <motion.div variants={itemVariants}>
                <Card className="bg-white border border-gray-200 shadow-sm">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <CreditCard className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">
                      Create Your Digital Business Card
                    </h2>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Stand out with a professional digital card. Share your contact info with a single tap.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button 
                        onClick={() => navigate('/create')}
                        className="bg-amber-600 hover:bg-amber-700 text-white px-8"
                        data-testid="create-card-btn"
                      >
                        Create Card
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => navigate('/shop')}
                        className="border-gray-300 text-gray-700"
                      >
                        Browse Templates
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </motion.div>
        ) : (
          /* ====== NOT LOGGED IN ====== */
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
              <Sparkles className="w-16 h-16 text-amber-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Piper Card</h2>
              <p className="text-gray-600 mb-6">Please sign in to view your dashboard</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Sign In Modal */}
      <SignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal}
      />
      
      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent className="sm:max-w-md" data-testid="delete-confirmation-modal">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <AlertDialogTitle className="text-xl">Delete Card</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-gray-600">
              This action cannot be undone. This will permanently delete <span className="font-semibold text-gray-900">"{cardToDelete?.name}"</span> and all associated data including analytics, visitor data, and saved contacts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <label htmlFor="delete-confirm-input" className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">delete</span> to confirm
            </label>
            <Input
              id="delete-confirm-input"
              type="text"
              placeholder="Type 'delete' here"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full border-gray-300 focus:border-red-500 focus:ring-red-500"
              data-testid="delete-confirm-input"
              autoComplete="off"
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setDeleteModalOpen(false);
                setCardToDelete(null);
                setDeleteConfirmText('');
              }}
              disabled={isDeleting}
              data-testid="delete-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleDeleteCard}
              disabled={deleteConfirmText.toLowerCase() !== 'delete' || isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
              data-testid="delete-confirm-btn"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Card
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default DashboardV2;
